import React, { useEffect, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import { apiJson } from '../utils/api';

interface Suppression {
  mark: string;
  reason?: string;
  created_at?: string;
  created_by?: string;
}

/** Owner-privacy: registration marks kept off the public community map. */
const SuppressionsPage: React.FC = () => {
  const [items, setItems] = useState<Suppression[]>([]);
  const [loading, setLoading] = useState(true);
  const [mark, setMark] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await apiJson<{ items: Suppression[] }>('/admin/suppressions');
      setItems(data.items ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const add = async (): Promise<void> => {
    if (!mark.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await apiJson('/admin/suppressions', {
        method: 'POST',
        body: JSON.stringify({ mark: mark.trim(), reason: reason.trim() || undefined }),
      });
      setMark('');
      setReason('');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await apiJson(`/admin/suppressions/${encodeURIComponent(m)}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Aircraft an owner has asked us to keep off the public community map. Requests arrive via the privacy contact; add the mark here and it is filtered from the map immediately."
        >
          Map suppressions
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error && <Alert type="error">{error}</Alert>}
        <Container header={<Header variant="h2">Suppress a registration mark</Header>}>
          <SpaceBetween size="s" direction="horizontal">
            <FormField label="Mark (US or Canada)">
              <Input
                value={mark}
                onChange={(e) => setMark(e.detail.value)}
                placeholder="N123AB or C-GABC"
              />
            </FormField>
            <FormField label="Reason (optional)">
              <Input
                value={reason}
                onChange={(e) => setReason(e.detail.value)}
                placeholder="owner request"
              />
            </FormField>
            <Button variant="primary" onClick={() => void add()} loading={busy} disabled={!mark.trim()}>
              Suppress
            </Button>
          </SpaceBetween>
        </Container>
        <Table
          loading={loading}
          items={items}
          columnDefinitions={[
            { id: 'mark', header: 'Mark', cell: (s: Suppression) => s.mark },
            { id: 'reason', header: 'Reason', cell: (s: Suppression) => s.reason ?? '—' },
            {
              id: 'created',
              header: 'Added',
              cell: (s: Suppression) => (s.created_at ? s.created_at.slice(0, 10) : '—'),
            },
            {
              id: 'actions',
              header: '',
              cell: (s: Suppression) => (
                <Button variant="link" onClick={() => void remove(s.mark)}>
                  Remove
                </Button>
              ),
            },
          ]}
          empty={
            <Box textAlign="center" color="text-body-secondary">
              No suppressed aircraft.
            </Box>
          }
          header={<Header counter={`(${items.length})`}>Suppressed aircraft</Header>}
        />
      </SpaceBetween>
    </ContentLayout>
  );
};

export default SuppressionsPage;
